// ── Footer Preview (extracted from FooterEditor) ────────────

// ── Types ────────────────────────────────────────────────────

export type FooterLayout =
  | "columns-4"
  | "columns-3"
  | "columns-2"
  | "centered"
  | "minimal";

export interface FooterOptions {
  showSocialLinks: boolean;
  showContactInfo: boolean;
  showQuickLinks: boolean;
  showLegalInfo: boolean;
  socialTitle: string;
  contactTitle: string;
  quickLinksTitle: string;
  legalTitle: string;
}

export interface QuickLink {
  source: "feature" | "page" | "custom";
  sourceId: string;
  label: string;
  url?: string;
  key: string;
}

export interface TenantAddress {
  key?: string;
  label?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

// ── Helpers ─────────────────────────────────────────────────

function readableText(hex: string): string {
  const h = hex.trim().replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(h)) return "#ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
}

/** Maximum number of sections a layout can display. */
function getLayoutCapacity(layout: FooterLayout): number {
  switch (layout) {
    case "columns-4": return 4;
    case "columns-3": return 3;
    case "columns-2": return 2;
    case "centered": return Infinity;
    case "minimal": return 0;
    default: return 4;
  }
}

const LAYOUT_LABELS: Record<string, string> = {
  "columns-4": "4 Columns",
  "columns-3": "3 Columns",
  "columns-2": "2 Columns",
  centered: "Centered",
  minimal: "Minimal",
};

/** Resolve quick link labels for the preview. */
function resolveQuickLinkLabels(
  quickLinks: QuickLink[],
  availablePages: { id: string; slug: string; title: string | null }[],
  availableFeatures: { path: string; label: string }[],
): string[] {
  if (quickLinks.length === 0) return ["About", "Services", "Blog"];
  return quickLinks
    .filter((link) => link.label || link.sourceId)
    .map((link) => {
      if (link.label) return link.label;
      if (link.source === "page") {
        const page = availablePages.find((p) => p.id === link.sourceId);
        return page?.title || page?.slug || "Page";
      }
      if (link.source === "feature") {
        const feature = availableFeatures.find((f) => f.path === link.sourceId);
        return feature?.label || link.sourceId;
      }
      return link.url || "Link";
    });
}

// ── Props ───────────────────────────────────────────────────

export interface FooterPreviewProps {
  layout: FooterLayout;
  options: FooterOptions;
  logoUrl: string | null;
  footerLogoUrl?: string | null;
  primaryColor: string;
  bgColor: string;
  tenantName: string;
  domain: string;
  phone: string | null;
  email: string | null;
  socialLinks: Record<string, string>;
  addresses: TenantAddress[];
  legalEntity: string | null;
  legalDisclaimers: string | null;
  quickLinks: QuickLink[];
  availablePages: { id: string; slug: string; title: string | null }[];
  availableFeatures: { path: string; label: string; key?: string }[];
  sectionOrder: string[];
}

// ── Main Preview ────────────────────────────────────────────

export function FooterPreview({
  layout,
  options,
  logoUrl,
  footerLogoUrl,
  primaryColor,
  bgColor,
  tenantName,
  domain,
  phone,
  email,
  socialLinks,
  addresses,
  legalEntity,
  legalDisclaimers,
  quickLinks,
  availablePages,
  availableFeatures,
  sectionOrder,
}: FooterPreviewProps) {
  const effectiveLogoUrl = footerLogoUrl || logoUrl;
  const textColor = readableText(bgColor);
  const mutedText = textColor === "#ffffff" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)";

  const socialEntries = Object.entries(socialLinks).filter(([, v]) => v.trim());
  const hasSocial = options.showSocialLinks && socialEntries.length > 0;
  const hasContact = options.showContactInfo && (phone || email || addresses.length > 0);

  const resolvedLinks = resolveQuickLinkLabels(
    quickLinks,
    availablePages,
    availableFeatures,
  );

  return (
    <aside className="lg:sticky lg:top-4">
      <div className="shadow-card overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b px-4 py-3">
          <h3 className="text-[14px] font-semibold">Preview</h3>
          <p className="mt-0.5 text-[11.5px] text-ink-4">
            {LAYOUT_LABELS[layout] ?? layout}{" "}
            layout
          </p>
        </div>

        <div className="p-4">
          {/* Mini browser */}
          <div className="hair overflow-hidden rounded-lg border bg-paper">
            {/* Browser chrome */}
            <div className="hair-b border-b flex items-center gap-2 bg-paper-2 px-3 py-2">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
                <span className="h-2 w-2 rounded-full bg-ink-5/50" />
              </div>
              <div className="hair flex flex-1 items-center rounded border bg-paper-hi px-2 py-1">
                <span className="truncate font-mono text-[10.5px] text-ink-4">
                  {domain || "yoursite.com"}
                </span>
              </div>
            </div>

            {/* Page content placeholder */}
            <div className="space-y-1.5 p-3">
              <div className="h-1.5 w-2/3 rounded bg-ink-5/15" />
              <div className="h-1.5 w-1/2 rounded bg-ink-5/10" />
            </div>

            {/* Footer */}
            <div
              className="px-3 py-4"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {layout === "minimal" ? (
                <MinimalFooterPreview
                  tenantName={tenantName}
                  mutedText={mutedText}
                  legalEntity={legalEntity}
                />
              ) : layout === "centered" ? (
                <CenteredFooterPreview
                  logoUrl={effectiveLogoUrl}
                  tenantName={tenantName}
                  options={options}
                  textColor={textColor}
                  mutedText={mutedText}
                  hasSocial={!!hasSocial}
                  hasContact={!!hasContact}
                  phone={phone}
                  email={email}
                  addresses={addresses}
                  legalDisclaimers={legalDisclaimers}
                  socialCount={socialEntries.length}
                  resolvedLinks={resolvedLinks}
                  sectionOrder={sectionOrder}
                />
              ) : (
                <ColumnsFooterPreview
                  layout={layout}
                  logoUrl={effectiveLogoUrl}
                  tenantName={tenantName}
                  options={options}
                  textColor={textColor}
                  mutedText={mutedText}
                  hasSocial={!!hasSocial}
                  hasContact={!!hasContact}
                  phone={phone}
                  email={email}
                  addresses={addresses}
                  primaryColor={primaryColor}
                  legalDisclaimers={legalDisclaimers}
                  socialCount={socialEntries.length}
                  resolvedLinks={resolvedLinks}
                  sectionOrder={sectionOrder}
                />
              )}
            </div>

            {/* System footer */}
            <div
              className="border-t px-3 py-2 text-center"
              style={{
                borderTopColor: textColor === "#ffffff" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
                backgroundColor: bgColor,
                color: mutedText,
              }}
            >
              <span className="text-[7px]">
                &copy; {new Date().getFullYear()} {legalEntity || tenantName}. All rights
                reserved.
              </span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

// ── Footer Layout Variants ──────────────────────────────────

function MinimalFooterPreview({
  tenantName,
  mutedText,
  legalEntity,
}: {
  tenantName: string;
  mutedText: string;
  legalEntity: string | null;
}) {
  return (
    <div className="text-center">
      <div className="text-[8px]" style={{ color: mutedText }}>
        &copy; {new Date().getFullYear()} {legalEntity || tenantName}
      </div>
      <div className="mt-1 flex justify-center gap-2">
        <span className="text-[7px]" style={{ color: mutedText }}>
          Terms
        </span>
        <span className="text-[7px]" style={{ color: mutedText }}>
          Privacy
        </span>
      </div>
    </div>
  );
}

function CenteredFooterPreview({
  logoUrl,
  tenantName,
  options,
  textColor,
  mutedText,
  hasSocial,
  hasContact,
  phone,
  email,
  addresses,
  legalDisclaimers,
  socialCount,
  resolvedLinks,
  sectionOrder,
}: {
  logoUrl: string | null;
  tenantName: string;
  options: FooterOptions;
  textColor: string;
  mutedText: string;
  hasSocial: boolean;
  hasContact: boolean;
  phone: string | null;
  email: string | null;
  addresses: TenantAddress[];
  legalDisclaimers: string | null;
  socialCount: number;
  resolvedLinks: string[];
  sectionOrder: string[];
}) {
  const enabledSections = sectionOrder.filter((key) => {
    if (key === "social") return hasSocial;
    if (key === "quickLinks") return options.showQuickLinks;
    if (key === "contact") return hasContact;
    if (key === "legal") return options.showLegalInfo && !!legalDisclaimers;
    return false;
  });

  const renderSection = (key: string) => {
    switch (key) {
      case "contact":
        return (
          <div className="text-[7px]" style={{ color: mutedText }}>
            {phone} {phone && email ? " | " : ""} {email}
            {addresses.length > 0 && addresses[0].city && (
              <div>
                {[addresses[0].city, addresses[0].state].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        );
      case "quickLinks":
        return (
          <div className="flex justify-center gap-2">
            {resolvedLinks.map((l) => (
              <span key={l} className="text-[7px]" style={{ color: mutedText }}>
                {l}
              </span>
            ))}
          </div>
        );
      case "social":
        return (
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: Math.min(socialCount, 5) }).map((_, i) => (
              <span
                key={i}
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: mutedText }}
              />
            ))}
          </div>
        );
      case "legal":
        return (
          <div className="text-[7px]" style={{ color: mutedText }}>
            {legalDisclaimers && (
              <div className="mt-0.5" style={{ maxWidth: "180px", margin: "2px auto 0" }}>
                {legalDisclaimers.length > 60
                  ? legalDisclaimers.slice(0, 60) + "..."
                  : legalDisclaimers}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="text-center space-y-2">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="mx-auto h-5 w-auto max-w-[80px] object-contain"
        />
      ) : (
        <div className="text-[9px] font-semibold" style={{ color: textColor }}>
          {tenantName}
        </div>
      )}
      {enabledSections.map((key) => (
        <div key={key}>{renderSection(key)}</div>
      ))}
    </div>
  );
}

function ColumnsFooterPreview({
  layout,
  logoUrl,
  tenantName,
  options,
  textColor,
  mutedText,
  hasSocial,
  hasContact,
  phone,
  email,
  addresses,
  primaryColor,
  legalDisclaimers,
  socialCount,
  resolvedLinks,
  sectionOrder,
}: {
  layout: FooterLayout;
  logoUrl: string | null;
  tenantName: string;
  options: FooterOptions;
  textColor: string;
  mutedText: string;
  hasSocial: boolean;
  hasContact: boolean;
  phone: string | null;
  email: string | null;
  addresses: TenantAddress[];
  primaryColor: string;
  legalDisclaimers: string | null;
  socialCount: number;
  resolvedLinks: string[];
  sectionOrder: string[];
}) {
  const cols =
    layout === "columns-4" ? 4 : layout === "columns-3" ? 3 : 2;

  // Build enabled sections in the user's order, capped by layout capacity
  const layoutCapacity = getLayoutCapacity(layout);
  const enabledSections = sectionOrder.filter((key) => {
    if (key === "social") return hasSocial;
    if (key === "quickLinks") return options.showQuickLinks;
    if (key === "contact") return hasContact;
    if (key === "legal") return options.showLegalInfo && !!legalDisclaimers;
    return false;
  }).slice(0, layoutCapacity);

  const renderSection = (key: string) => {
    switch (key) {
      case "social":
        return (
          <div>
            <div className="mb-1 text-[7px] font-semibold" style={{ color: textColor }}>
              {options.socialTitle}
            </div>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(socialCount, 5) }).map((_, i) => (
                <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
              ))}
            </div>
          </div>
        );
      case "quickLinks":
        return (
          <div>
            <div className="mb-1 text-[7px] font-semibold" style={{ color: textColor }}>
              {options.quickLinksTitle}
            </div>
            {resolvedLinks.map((l) => (
              <div key={l} className="text-[6px]" style={{ color: mutedText }}>{l}</div>
            ))}
          </div>
        );
      case "contact":
        return (
          <div>
            <div className="mb-1 text-[7px] font-semibold" style={{ color: textColor }}>
              {options.contactTitle}
            </div>
            {phone && <div className="text-[6px]" style={{ color: mutedText }}>{phone}</div>}
            {email && <div className="text-[6px]" style={{ color: mutedText }}>{email}</div>}
            {addresses.length > 0 && addresses[0].city && (
              <div className="text-[6px]" style={{ color: mutedText }}>
                {[addresses[0].city, addresses[0].state].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        );
      case "legal":
        return (
          <div>
            {legalDisclaimers && (
              <div className="text-[6px] mt-0.5" style={{ color: mutedText }}>
                {legalDisclaimers.length > 40 ? legalDisclaimers.slice(0, 40) + "..." : legalDisclaimers}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // First section shares column with brand; rest get their own columns.
  // Last column absorbs overflow when there are more sections than column slots.
  const firstSection = enabledSections[0] ?? null;
  const restSections = enabledSections.slice(1);
  const availableCols = cols - 1; // columns after brand
  const overflowStart = Math.max(0, availableCols - 1);

  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {/* Brand column + first section */}
      <div>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="mb-1.5 h-4 w-auto max-w-[50px] object-contain"
          />
        ) : (
          <div
            className="mb-1.5 text-[8px] font-semibold"
            style={{ color: textColor }}
          >
            {tenantName}
          </div>
        )}
        {firstSection && renderSection(firstSection)}
      </div>

      {/* Individual section columns */}
      {restSections.slice(0, overflowStart).map((key) => (
        <div key={key}>{renderSection(key)}</div>
      ))}

      {/* Last column (may stack remaining sections) */}
      {restSections.length > overflowStart && (
        <div>
          {restSections.slice(overflowStart).map((key) => (
            <div key={key} className={restSections.length - overflowStart > 1 ? "mb-1.5" : ""}>
              {renderSection(key)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
